import { useEffect, useRef, useState } from "react";
import {
  Activity,
  BarChart3,
  Calendar,
  CheckCircle2,
  FileText,
  Linkedin,
  Loader2,
  Search,
  Sparkles,
  TrendingUp,
  Twitter,
  Zap,
} from "lucide-react";

/* ────────────── Timeline ────────────── */

const BEAT_MS = 2600;
const BEATS = 4;
const LOOP_MS = BEAT_MS * BEATS;

type Target = { x: number; y: number; click?: boolean };

const leftTargets: Record<number, Target[]> = {
  0: [
    { x: 10, y: 18 },
    { x: 32, y: 18 },
    { x: 56, y: 18 },
    { x: 80, y: 18, click: true },
  ],
  1: [{ x: 50, y: 62, click: true }],
  2: [
    { x: 50, y: 50 },
    { x: 80, y: 82, click: true },
  ],
  3: [
    { x: 22, y: 50 },
    { x: 56, y: 50 },
    { x: 86, y: 50, click: true },
  ],
};

const rightTargets: Record<number, Target[]> = {
  0: [{ x: 88, y: 12 }],
  1: [{ x: 86, y: 14, click: true }],
  2: [{ x: 88, y: 12 }],
  3: [{ x: 88, y: 12, click: true }],
};

const beatLabels = ["Research", "Draft", "Post", "Report"];

function easeInOutCubic(t: number) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function targetAt(targets: Target[] | undefined, tick: number) {
  if (!targets || targets.length === 0) return { x: 50, y: 50, click: false };
  if (targets.length === 1) return { ...targets[0] };
  const seg = 1 / (targets.length - 1);
  const idx = Math.min(targets.length - 2, Math.floor(tick / seg));
  const localT = (tick - idx * seg) / seg;
  const e = easeInOutCubic(Math.min(1, Math.max(0, localT)));
  const a = targets[idx];
  const b = targets[idx + 1];
  return {
    x: lerp(a.x, b.x, e),
    y: lerp(a.y, b.y, e),
    click: localT > 0.92 ? !!b.click : false,
  };
}

/* ────────────── Cursor sprite ────────────── */

function CursorSprite({
  pos,
  label,
  flash,
  tone = "neutral",
}: {
  pos: { x: number; y: number };
  label: string;
  flash: boolean;
  tone?: "neutral" | "brand";
}) {
  return (
    <div
      className="pointer-events-none absolute z-30 transition-[left,top] duration-200 ease-linear"
      style={{ left: `${pos.x}%`, top: `${pos.y}%` }}
      aria-hidden
    >
      <div className="relative">
        {flash && (
          <span
            className="absolute left-0 top-0 size-6 rounded-full"
            style={{
              background:
                tone === "brand"
                  ? "radial-gradient(circle, rgba(244,114,208,0.55), transparent 70%)"
                  : "radial-gradient(circle, rgba(255,255,255,0.45), transparent 70%)",
              animation: "cursor-click-ring 500ms ease-out forwards",
            }}
          />
        )}
        <svg
          width="16"
          height="18"
          viewBox="0 0 16 18"
          className="drop-shadow-[0_2px_4px_rgba(0,0,0,0.6)]"
        >
          <path
            d="M1 1 L1 14 L4.5 11 L7 16.5 L9 15.5 L6.5 10 L11 10 Z"
            fill="#fff"
            stroke="#000"
            strokeWidth="0.8"
            strokeLinejoin="round"
          />
        </svg>
        <span
          className={`absolute left-4 top-4 px-1.5 py-0.5 rounded-md text-[9px] font-semibold tracking-wide whitespace-nowrap ${
            tone === "brand"
              ? "bg-gradient-to-r from-pink-soft to-violet text-white"
              : "bg-white/90 text-black"
          }`}
        >
          {label}
        </span>
      </div>
    </div>
  );
}

/* ────────────── Before panel ────────────── */

function PanelChrome({
  title,
  badge,
  children,
  desaturate = false,
  tone = "neutral",
}: {
  title: string;
  badge: React.ReactNode;
  children: React.ReactNode;
  desaturate?: boolean;
  tone?: "neutral" | "manual" | "axy";
}) {
  const titleClass =
    tone === "axy" ? "text-gradient" : tone === "manual" ? "text-orange-300" : "text-white";
  return (
    <div
      className="relative glass-card overflow-hidden"
      style={{
        filter: desaturate ? "saturate(0.55) brightness(0.92)" : undefined,
      }}
    >
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-white/10 bg-white/[0.04]">
        <div className="flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
          <span className="size-2.5 rounded-full bg-white/15" />
        </div>
        <span className={`text-sm md:text-base font-semibold tracking-tight ${titleClass}`}>
          {title}
        </span>
        {badge}
      </div>
      <div className="relative aspect-[4/3] sm:aspect-[16/11]">{children}</div>
    </div>
  );
}

function BeforePanel({ beat }: { beat: number }) {
  const tabs = [
    { icon: BarChart3, name: "Analytics" },
    { icon: Search, name: "Search" },
    { icon: Linkedin, name: "LinkedIn" },
    { icon: Twitter, name: "X" },
  ];
  const activeTab = beat === 0 ? Math.min(3, beat) : 3;

  // Doc typing for beat 1
  const draftText = beat >= 1 ? "Excited to share some thoughts on AI and" : "";
  const wordCount = draftText.split(" ").filter(Boolean).length;

  return (
    <PanelChrome
      title="Manual marketing"
      tone="manual"
      desaturate
      badge={
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[9px] uppercase tracking-wider bg-orange-500/20 text-orange-300 border border-orange-500/30">
          <span className="size-1.5 rounded-full bg-orange-400 animate-pulse" />
          stale 32d
        </span>
      }
    >
      {/* Tab strip */}
      <div className="absolute inset-x-0 top-0 flex border-b border-white/10 bg-white/[0.02]">
        {tabs.map((t, i) => {
          const isActive = i === activeTab;
          return (
            <div
              key={t.name}
              className={`flex-1 flex items-center justify-center gap-1.5 py-2 text-[10px] font-medium border-r border-white/10 last:border-r-0 ${
                isActive ? "bg-white/[0.05] text-white/85" : "text-white/45"
              }`}
            >
              <t.icon className="size-3" />
              <span className="hidden sm:inline">{t.name}</span>
            </div>
          );
        })}
      </div>

      {/* Content per beat */}
      <div className="absolute inset-0 pt-9 px-4 pb-4">
        {beat === 0 && (
          <div className="h-full flex flex-col items-center justify-center gap-2">
            <Loader2 className="size-6 text-white/40 animate-spin" />
            <p className="text-xs text-white/45">Loading dashboard…</p>
            <p className="text-[10px] text-white/30">tab 4 of 6</p>
          </div>
        )}
        {beat === 1 && (
          <div className="h-full flex flex-col">
            <div className="flex items-center justify-between text-[10px] text-white/40 mb-2">
              <span>Untitled doc · draft</span>
              <span>{wordCount} words</span>
            </div>
            <div className="flex-1 rounded-md bg-white/[0.03] border border-white/10 p-3 text-xs text-white/65 font-mono leading-relaxed">
              {draftText}
              <span
                className="inline-block w-1.5 h-3 ml-0.5 align-middle bg-white/70"
                style={{ animation: "caret-blink 1s steps(2) infinite" }}
              />
            </div>
            <p className="mt-2 text-[10px] text-white/40">Generic AI output · no point of view</p>
          </div>
        )}
        {beat === 2 && (
          <div className="h-full flex flex-col">
            <div className="flex items-center gap-2 text-[10px] text-white/55 mb-2">
              <Calendar className="size-3" /> Schedule post
            </div>
            <div className="grid grid-cols-7 gap-1 flex-1">
              {Array.from({ length: 21 }).map((_, i) => (
                <div
                  key={i}
                  className={`rounded-sm border border-white/10 ${
                    i === 11 ? "bg-white/15 text-white" : "bg-white/[0.03] text-white/30"
                  } text-[8px] grid place-items-center`}
                >
                  {i + 1}
                </div>
              ))}
            </div>
            <div className="mt-2 flex justify-end">
              <span className="px-2 py-1 rounded-md text-[10px] bg-white/10 text-white/70 border border-white/15">
                Schedule for next Tue
              </span>
            </div>
          </div>
        )}
        {beat === 3 && (
          <div className="h-full grid grid-cols-3 gap-2">
            {["GA4", "Search", "LinkedIn"].map((name, i) => (
              <div
                key={name}
                className="rounded-md border border-white/10 bg-white/[0.03] p-2 flex flex-col"
              >
                <p className="text-[9px] text-white/50">{name}</p>
                <div className="flex-1 flex items-end gap-0.5 mt-1">
                  {[0.3, 0.5, 0.4, 0.6, 0.45, 0.5].map((h, j) => (
                    <div
                      key={j}
                      className="flex-1 bg-white/20 rounded-sm"
                      style={{ height: `${h * 100}%` }}
                    />
                  ))}
                </div>
                <p className="text-[8px] text-orange-300/80 mt-1">
                  refreshed 32d ago{i === 2 ? " ⚠" : ""}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </PanelChrome>
  );
}

/* ────────────── After panel ────────────── */

const axyCards = [
  {
    icon: TrendingUp,
    title: "Signal detected",
    body: "“agentic GTM” +43% WoW",
    pill: "auto",
  },
  {
    icon: Sparkles,
    title: "Strategy drafted",
    body: "Founder POV thread + GEO article",
    pill: "auto",
  },
  {
    icon: Zap,
    title: "Post published",
    body: "LinkedIn · 18.4k reach so far",
    pill: "live",
  },
  {
    icon: BarChart3,
    title: "Performance synced",
    body: "+18% vs last week · all channels",
    pill: "live",
  },
];

function AfterPanel({ beat }: { beat: number }) {
  return (
    <PanelChrome
      title="Axy.digital autopilot"
      tone="axy"
      badge={
        <span className="inline-flex items-center gap-1.5 px-1.5 py-0.5 rounded-md text-[9px] uppercase tracking-wider bg-gradient-to-r from-pink-soft/30 to-violet/30 text-white border border-white/20">
          <span className="size-1.5 rounded-full bg-pink-soft animate-pulse" />
          engine · live
        </span>
      }
    >
      <div className="absolute inset-0 p-3 flex flex-col gap-2">
        {axyCards.map((c, i) => {
          const visible = i <= beat;
          return (
            <div
              key={c.title}
              className="rounded-lg border border-white/10 bg-gradient-to-r from-pink-soft/10 to-violet/10 px-3 py-2 flex items-center gap-3"
              style={{
                opacity: visible ? 1 : 0,
                animation: visible
                  ? `card-slide-up 500ms cubic-bezier(.2,.8,.2,1) both`
                  : undefined,
                animationDelay: visible ? "0ms" : undefined,
              }}
            >
              <span className="grid place-items-center size-7 rounded-lg shrink-0 bg-gradient-to-br from-pink-soft/40 to-violet/40 border border-white/15">
                <c.icon className="size-3.5 text-white" />
              </span>
              <div className="min-w-0 flex-1">
                <p className="text-[11px] font-semibold text-white truncate">{c.title}</p>
                <p className="text-[10px] text-white/65 truncate">{c.body}</p>
              </div>
              <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-md bg-white/10 text-white/75 border border-white/10 shrink-0">
                {c.pill}
              </span>
            </div>
          );
        })}
        <div className="mt-auto flex items-center gap-2 text-[10px] text-white/55">
          <Activity className="size-3 text-pink-soft" />
          <span>0 manual steps · loop running</span>
          <CheckCircle2 className="size-3 text-pink-soft ml-auto" />
        </div>
      </div>
    </PanelChrome>
  );
}

/* ────────────── Main ────────────── */

export function ProblemSplit() {
  const containerRef = useRef<HTMLDivElement>(null);
  const leftWrapRef = useRef<HTMLDivElement>(null);
  const rightWrapRef = useRef<HTMLDivElement>(null);
  const [beat, setBeat] = useState(0);
  const [leftPos, setLeftPos] = useState<{ x: number; y: number }>({ x: 10, y: 18 });
  const [rightPos, setRightPos] = useState<{ x: number; y: number }>({ x: 88, y: 12 });
  const [leftFlash, setLeftFlash] = useState(false);
  const [rightFlash, setRightFlash] = useState(false);
  const [paused, setPaused] = useState(false);
  const [reduced, setReduced] = useState(false);

  // reduced motion
  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReduced(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  // pause when offscreen
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(([e]) => setPaused(!e.isIntersecting), { threshold: 0.15 });
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // pause when tab hidden
  useEffect(() => {
    const onVis = () => setPaused((p) => (document.hidden ? true : p));
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  // animation loop
  useEffect(() => {
    if (reduced) {
      setBeat(BEATS - 1);
      return;
    }
    if (paused) return;
    let raf = 0;
    const start = performance.now();
    let lastBeat = -1;
    let lastLeftClick = -1;
    let lastRightClick = -1;

    const tick = (now: number) => {
      const elapsed = (now - start) % LOOP_MS;
      const b = Math.floor(elapsed / BEAT_MS);
      const localTick = (elapsed - b * BEAT_MS) / BEAT_MS;

      if (b !== lastBeat) {
        lastBeat = b;
        setBeat(b);
      }

      const lt = targetAt(leftTargets[b], localTick);
      const rt = targetAt(rightTargets[b], localTick);
      setLeftPos({ x: lt.x, y: lt.y });
      setRightPos({ x: rt.x, y: rt.y });

      if (lt.click && lastLeftClick !== b) {
        lastLeftClick = b;
        setLeftFlash(true);
        setTimeout(() => setLeftFlash(false), 500);
      }
      if (rt.click && lastRightClick !== b) {
        lastRightClick = b;
        setRightFlash(true);
        setTimeout(() => setRightFlash(false), 500);
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [paused, reduced]);

  return (
    <div
      ref={containerRef}
      className="relative max-w-6xl mx-auto"
      role="img"
      aria-label="Animation comparing manual marketing work to Axy.digital's autonomous engine."
    >
      <div className="grid lg:grid-cols-2 gap-5">
        <div ref={leftWrapRef} className="relative">
          <BeforePanel beat={beat} />
          {!reduced && <CursorSprite pos={leftPos} label="you" flash={leftFlash} tone="neutral" />}
        </div>
        <div ref={rightWrapRef} className="relative">
          <AfterPanel beat={beat} />
          {!reduced && <CursorSprite pos={rightPos} label="Axy.digital" flash={rightFlash} tone="brand" />}
        </div>
      </div>

      {/* Beat caption strip */}
      <div className="mt-6 grid grid-cols-4 gap-2 max-w-3xl mx-auto">
        {beatLabels.map((label, i) => {
          const isActive = i === beat;
          return (
            <div
              key={label}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all duration-300 ${
                isActive
                  ? "border-white/25 bg-gradient-to-r from-pink-soft/15 to-violet/15"
                  : "border-white/10 bg-white/[0.03]"
              }`}
            >
              <span
                className={`text-[10px] tabular-nums font-semibold ${
                  isActive ? "text-gradient" : "text-white/40"
                }`}
              >
                0{i + 1}
              </span>
              <span
                className={`text-[11px] font-medium ${isActive ? "text-white" : "text-white/55"}`}
              >
                {label}
              </span>
            </div>
          );
        })}
      </div>

      <p className="mt-4 text-center text-xs text-white/55" aria-live="polite">
        Same 8 hours. <span className="text-gradient font-semibold">Different operator.</span>
      </p>
    </div>
  );
}
